import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Package, RefreshCw, WifiOff, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

const rupees = (p) => `₹${((p || 0) / 100).toLocaleString("en-IN")}`;
const NEXT = { placed: "confirmed", confirmed: "shipped", shipped: "delivered" };
const STATUS_TONE = { placed: "bg-amber-100 text-amber-900", confirmed: "bg-sky-100 text-sky-900", shipped: "bg-indigo-100 text-indigo-900",
  delivered: "bg-emerald-100 text-emerald-900", cancelled: "bg-stone-200 text-stone-700" };

function OrderCard({ o, role, onChanged, highlight }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const pv = o.payment_view || {};
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); onChanged(); }
    catch (e) { if (navigator.onLine) toast.error(e?.response?.data?.detail || t("orders.action_failed", "Couldn't update the order")); }
    finally { setBusy(false); }
  };
  const pay = () => run(async () => {
    const r = await api.post(`/commerce/orders/${o.order_id}/pay`);
    await api.post("/findpay/mock/complete", { intent_id: r.data.intent_id, state: "SUCCESS" });
  }, t("orders.paid_test", "Test payment recorded · no real money moved"));
  const next = NEXT[o.status];
  const canCancel = role === "seller" ? ["placed", "confirmed"].includes(o.status) && pv.status !== "paid" : o.status === "placed" && pv.status !== "paid";
  return (
    <div className={`bg-white border rounded-2xl p-3 ${highlight ? "border-[#E05E36] ring-2 ring-[#E05E36]/20" : "border-stone-200"}`} data-testid={`order-${o.order_id}`}>
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="font-mono text-stone-500 truncate">{o.order_id}</span>
        <span className={`font-bold uppercase text-[10px] px-2 py-0.5 rounded ${STATUS_TONE[o.status] || "bg-stone-100"}`} data-testid={`order-status-${o.order_id}`}>
          {t(`orders.status.${o.status}`, o.status)}
        </span>
      </div>
      {role === "seller" && <div className="text-sm font-bold mt-1">{o.buyer_name}</div>}
      <div className="text-xs text-stone-600 mt-0.5">{o.lines?.map((l) => `${l.name} × ${l.qty}`).join(", ")}</div>
      {role === "seller" && o.buyer_address && <div className="text-[11px] text-stone-500 mt-0.5" data-testid={`order-address-${o.order_id}`}>{t("orders.deliver_to", "Deliver to")}: {o.buyer_address}</div>}
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <span className="font-black text-[#E05E36]">{rupees(o.total_paise)}</span>
        <span className="text-[11px] text-stone-600" data-testid={`order-payment-${o.order_id}`}>
          {o.payment_mode === "cod" ? t("orders.cod", "Cash on delivery") : `FindPay · ${t(`orders.pay.${pv.status}`, pv.status || "pending")}`}
        </span>
        {pv.test_mode && <span className="rounded-full bg-amber-200 text-amber-900 px-2 text-[10px] font-black" data-testid={`order-test-${o.order_id}`}>{t("pay.test_mode", "TEST MODE · no real money")}</span>}
        <span className="text-[10px] text-stone-500 ms-auto">{new Date(o.created_at).toLocaleString()}</span>
      </div>
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        {o.conv_id && <Link to={`/messenger/${o.conv_id}`} className="text-xs font-bold text-sky-700 inline-flex items-center gap-1 underline" data-testid={`order-chat-${o.order_id}`}><MessageCircle size={12} />{t("orders.open_chat", "Order chat")}</Link>}
        {role === "buyer" && o.payment_mode === "findpay" && ["placed", "confirmed"].includes(o.status) && pv.status !== "paid" && (
          <button disabled={busy} onClick={pay} className="h-8 px-3 rounded-full bg-stone-900 text-white text-xs font-bold disabled:opacity-50" data-testid={`order-pay-${o.order_id}`}>
            {t("orders.pay_test", "Pay (test mode)")}
          </button>
        )}
        {role === "seller" && next && !(o.payment_mode === "findpay" && pv.status !== "paid" && next !== "confirmed") && (
          <button disabled={busy} onClick={() => run(() => api.post(`/commerce/orders/${o.order_id}/status`, { status: next }))}
                  className="h-8 px-3 rounded-full bg-stone-900 text-white text-xs font-bold disabled:opacity-50" data-testid={`order-advance-${o.order_id}`}>
            {t(`orders.mark.${next}`, `Mark ${next}`)}
          </button>
        )}
        {canCancel && (
          <button disabled={busy} data-testid={`order-cancel-${o.order_id}`}
                  onClick={() => window.confirm(t("orders.cancel_confirm", "Cancel this order?")) && run(() => api.post(`/commerce/orders/${o.order_id}/status`, { status: "cancelled" }))}
                  className="h-8 px-3 rounded-full border border-stone-300 text-xs font-bold disabled:opacity-50">{t("orders.cancel", "Cancel")}</button>
        )}
      </div>
    </div>
  );
}

// /orders · "My purchases" grouped by seller + "Sales" (only the signed-in seller's own orders; server-scoped).
export function OrdersInbox() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "sales" ? "sales" : "purchases";
  const [state, setState] = useState({ items: null, error: false });
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true), down = () => setOnline(false);
    window.addEventListener("online", up); window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);
  const load = useCallback(async () => {
    try { const r = await api.get("/commerce/orders", { params: { role: tab === "sales" ? "seller" : "buyer" } }); setState({ items: r.data, error: false }); }
    catch { setState((s) => ({ ...s, error: true })); }
  }, [tab]);
  useEffect(() => { if (user) { setState({ items: null, error: false }); load(); } }, [user, load]);
  const groups = useMemo(() => {
    if (tab === "sales" || !state.items) return null;
    const g = {};
    state.items.forEach((o) => { (g[o.seller_id] = g[o.seller_id] || { name: o.seller_name || t("orders.seller", "Seller"), orders: [] }).orders.push(o); });
    return Object.entries(g);
  }, [state.items, tab, t]);
  const hl = params.get("order") || params.get("highlight");
  useEffect(() => {   // deep link from a journey card / alert: bring that order into view
    if (hl && state.items) document.querySelector(`[data-testid="order-${hl}"]`)?.scrollIntoView({ block: "center" });
  }, [hl, state.items]);
  if (!user) return <div className="p-12 text-center">{t("orders.sign_in", "Sign in to see your orders.")}</div>;
  return (
    <div className="max-w-3xl mx-auto px-4 pt-4 pb-40" data-testid="orders-page">
      <h1 className="heading text-2xl font-black text-stone-900">{t("orders.title", "Orders")}</h1>
      <div className="flex gap-2 my-3" role="tablist">
        {[["purchases", t("orders.tab.purchases", "My purchases")], ["sales", t("orders.tab.sales", "Sales")]].map(([k, label]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setParams(k === "sales" ? { tab: "sales" } : {})} data-testid={`orders-tab-${k}`}
                  className={`h-9 px-4 rounded-full text-sm font-bold transition-colors ${tab === k ? "bg-stone-900 text-white" : "bg-white border border-stone-200"}`}>{label}</button>
        ))}
      </div>
      {!online && <div className="mb-3 rounded-xl bg-stone-900 text-white text-xs px-3 py-2 flex items-center gap-2" data-testid="orders-offline"><WifiOff size={14} />{t("inbox.offline", "You're offline — showing what was last loaded.")}</div>}
      {state.items === null && !state.error ? <div className="text-center py-16" data-testid="orders-loading"><Loader2 className="inline animate-spin" /></div>
        : state.items === null ? (
          <div className="text-center py-14 text-stone-600" data-testid="orders-error">
            <p className="text-sm mb-3">{t("orders.load_failed", "Couldn't load your orders.")}</p>
            <button onClick={() => { setState({ items: null, error: false }); load(); }} data-testid="orders-retry"
                    className="h-9 px-4 rounded-full border border-stone-300 font-bold text-sm inline-flex items-center gap-1.5"><RefreshCw size={14} />{t("common.retry", "Retry")}</button>
          </div>
        ) : state.items.length === 0 ? (
          <div className="text-center py-16 text-stone-500" data-testid="orders-empty"><Package className="mx-auto mb-2 text-stone-300" size={32} />
            {tab === "sales" ? t("orders.empty_sales", "No sales yet.") : t("orders.empty_purchases", "No purchases yet.")}</div>
        ) : tab === "sales" ? (
          <div className="space-y-2" data-testid="orders-sales-list">{state.items.map((o) => <OrderCard key={o.order_id} o={o} role="seller" onChanged={load} highlight={hl === o.order_id} />)}</div>
        ) : (
          <div className="space-y-4" data-testid="orders-purchases-list">
            {state.error && <div className="text-xs text-red-700" data-testid="orders-partial-error">{t("orders.refresh_failed", "Couldn't refresh — showing last loaded orders.")}</div>}
            {groups.map(([sid, g]) => (
              <section key={sid} data-testid={`orders-group-${sid}`}>
                <div className="text-[11px] font-black uppercase tracking-widest text-stone-500 mb-1.5">{g.name} · {g.orders.length}</div>
                <div className="space-y-2">{g.orders.map((o) => <OrderCard key={o.order_id} o={o} role="buyer" onChanged={load} highlight={hl === o.order_id} />)}</div>
              </section>
            ))}
          </div>
        )}
    </div>
  );
}
